// eslint-disable-next-line no-restricted-imports
import { Switch } from "@headlessui/react";
import React, { useEffect, useMemo, useState } from "react";

import {
  FailedApiRequestsTD,
  PageLoadStatsTD,
  PageWorksheetLoadErrorTD,
  PoorConnectionWarningTD,
  WebsocketDisconnectionsTD,
} from "./Table";

import { serverGetMonitoringStats, serverGetSectionUserCounts } from "api/api-monitoring";
import { serverUpdateSectionForMonitoring } from "api/api-server-actions";
import { serverGetSectionsForMonitoring } from "api/api-server-data";
import { SearchableDropdown } from "components/monitoring/SearchableDropdown";
import { ClassSessionCategory, SectionMonitoringData, SectionType } from "components/server-types";
import { Button } from "mds/components/Button";
import { Menu, MenuItem } from "mds/components/Menu";
import { Tooltip } from "mds/components/Tooltip";
import { RoundedButton } from "mds/components/buttons/RoundedButton";
import {
  BookSectionIcon,
  ChevronDownIcon,
  CircleErrorRedIcon,
  CircleInfoBlueIcon,
  CircleSuccessGreenIcon,
  HomeHouseIcon,
} from "mds/icons";
import { useAppDispatch } from "store/index";
import { monitoringStateActions } from "store/slices/monitoring";
import { Link, useQueryParamsManager } from "utils/routing";
import { getUserFullName } from "utils/user-utils";

const SectionRow: React.FC<{
  section: SectionMonitoringData;
  sectionUserCounts: Record<string, number>;
}> = ({ section, sectionUserCounts }) => {
  const [avEnabled, setAvEnabled] = useState(section.av_enabled);
  const [avService, setAvService] = useState(section.av_service);
  const [isEditing, setIsEditing] = useState(false);

  const updateSection = async (data: Partial<SectionType>) => {
    const updatedSection = await serverUpdateSectionForMonitoring(section.id, data);
    setAvEnabled(updatedSection.av_enabled);
    setAvService(updatedSection.av_service);
  };

  return (
    <tr key={section.id}>
      <td className="w-[200px] pr-[1px]">
        <Link
          className="block text-blue hover:text-blue-shade-40"
          to={`/monitoring/section/${section.id}`}
        >
          {section.title}
        </Link>
      </td>
      <td className="w-[200px] truncate pr-[1px]">
        {section.instructor ? (
          <span className="bg-blue-100 text-blue-800">
            {getUserFullName(section.instructor.user)}
          </span>
        ) : (
          <span className="text-gray-400">-</span>
        )}
      </td>
      <td className="w-[80px] pr-[1px]">{section.student_count}</td>
      <td className="w-[80px] pr-[1px]">{sectionUserCounts[section.id] || 0}</td>
      <PageLoadStatsTD sectionId={section.id} />
      <WebsocketDisconnectionsTD sectionId={section.id} />
      <FailedApiRequestsTD sectionId={section.id} />
      <PageWorksheetLoadErrorTD sectionId={section.id} />
      <PoorConnectionWarningTD sectionId={section.id} />
      <td className="w-[80px] pr-[1px]">
        {!!section.class_session?.presenting_user_id && !section.class_session.is_practice ? (
          <CircleSuccessGreenIcon />
        ) : (
          <CircleErrorRedIcon />
        )}
      </td>
      <td className="w-[80px] pr-[1px]">
        {section.class_session?.category === ClassSessionCategory.VIRTUAL ? (
          <CircleSuccessGreenIcon />
        ) : (
          <CircleErrorRedIcon />
        )}
      </td>
      <td className="w-[80px] pr-[1px]">
        {isEditing ? (
          <select
            className="border-gray-300 focus:ring-blue-500 w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2"
            value={avEnabled ? "enabled" : "disabled"}
            onChange={(e) => {
              const newValue = e.target.value === "enabled";
              setAvEnabled(newValue);
              updateSection({ av_enabled: newValue });
            }}
          >
            <option value="enabled">Yes</option>
            <option value="disabled">No</option>
          </select>
        ) : (
          <span className="inline-flex items-center justify-center">
            {avEnabled ? <CircleSuccessGreenIcon /> : <CircleErrorRedIcon />}
          </span>
        )}
      </td>
      <td className="w-[120px] pr-[1px]">
        {avEnabled ? (
          isEditing ? (
            <select
              className="border-gray-300 focus:ring-blue-500 w-full rounded border px-2 py-1 text-sm focus:outline-none focus:ring-2"
              value={avService}
              onChange={(e) => {
                setAvService(e.target.value);
                updateSection({ av_service: e.target.value });
              }}
            >
              <option value="daily">Daily</option>
              <option value="licode">Licode</option>
              <option value="opentok">OpenTok</option>
            </select>
          ) : (
            avService || "-"
          )
        ) : (
          "-"
        )}
      </td>
      <td className="w-[240px] pr-[1px]">
        {avEnabled && avService === "licode" ? (
          <span className="bg-gray-100 rounded px-2 py-1 text-xs">
            {section.class_session?.licode_server_url}
          </span>
        ) : (
          "-"
        )}
      </td>
      <td className="w-[20px] pr-[1px] text-right">
        <div className="flex justify-end">
          <RoundedButton className="gap-2" kind="toggle" onClick={() => setIsEditing(!isEditing)}>
            Edit Mode
            <Switch
              as="div"
              checked={isEditing}
              className="group flex h-3.5 w-7 min-w-7 cursor-pointer items-center rounded-full bg-black-tint-70 transition data-[checked]:bg-blue data-[checked][data-disabled]:bg-black-tint-70"
            >
              <span className="size-3 translate-x-[0.0675rem] rounded-full bg-white shadow-md transition group-data-[checked]:translate-x-[0.9375rem]" />
            </Switch>
          </RoundedButton>
        </div>
      </td>
    </tr>
  );
};

export const SectionsMonitoringTable: React.FC = () => {
  const dispatch = useAppDispatch();
  const { setQueryParams, getQueryParam } = useQueryParamsManager();
  const selectedOrgId = getQueryParam("org");
  const selectedSectionId = getQueryParam("course");
  const showActiveOnly = getQueryParam("active") === "1";
  const [sections, setSections] = useState<SectionMonitoringData[]>([]);
  const [sectionUserCounts, setSectionUserCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<
    keyof SectionMonitoringData | "presenting" | "virtual_class" | "online_users"
  >("title");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  useEffect(() => {
    const fetchSections = async (initial = false) => {
      try {
        if (initial) setLoading(true);
        const sectionData = await serverGetSectionsForMonitoring();
        setSections(sectionData);
      } catch (err) {
        setError(err instanceof Error ? err.message : "Failed to fetch sections");
      } finally {
        if (initial) setLoading(false);
      }
    };

    // Initial fetch
    fetchSections(true);

    // Set up polling every minute
    const interval = setInterval(fetchSections, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchSectionUserCounts = async () => {
      try {
        const data = await serverGetSectionUserCounts();
        setSectionUserCounts(data);
      } catch (err) {
        console.error("Failed to fetch section user counts:", err);
      }
    };

    // Initial fetch
    fetchSectionUserCounts();

    // Set up polling every minute
    const interval = setInterval(fetchSectionUserCounts, 60000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const fetchMonitoringStats = async () => {
      const stats = await serverGetMonitoringStats();
      dispatch(monitoringStateActions.setMonitoringStats(stats));
    };

    // Inital fetch
    fetchMonitoringStats();

    // Set up polling every minute
    const interval = setInterval(fetchMonitoringStats, 60000);

    return () => clearInterval(interval);
  }, [dispatch]);

  // Filter sections by selected org, section, and active status
  const filteredSections = useMemo(() => {
    let filtered = sections;

    if (selectedOrgId) {
      filtered = filtered.filter((section) => section.org.id === selectedOrgId);
    }

    if (selectedSectionId) {
      filtered = filtered.filter((section) => section.id === selectedSectionId);
    }

    if (showActiveOnly) {
      filtered = filtered.filter((section) => {
        return sectionUserCounts[section.id] > 0;
      });
    }

    return filtered;
  }, [sections, selectedOrgId, selectedSectionId, showActiveOnly, sectionUserCounts]);

  // Sort sections based on current sort state
  const sortedFilteredSections = useMemo(() => {
    if (!sortColumn) return filteredSections;

    return [...filteredSections].sort((a, b) => {
      let aValue: string | number | boolean | null;
      let bValue: string | number | boolean | null;

      // Extract values for sorting based on column
      switch (sortColumn) {
        case "title":
          aValue = a.title;
          bValue = b.title;
          break;
        case "instructor":
          aValue = a.instructor ? getUserFullName(a.instructor.user) : "";
          bValue = b.instructor ? getUserFullName(b.instructor.user) : "";
          break;
        case "student_count":
          aValue = a.student_count;
          bValue = b.student_count;
          break;
        case "av_enabled":
          aValue = a.av_enabled;
          bValue = b.av_enabled;
          break;
        case "av_service":
          aValue = a.av_service || "";
          bValue = b.av_service || "";
          break;
        case "presenting":
          aValue = !!a.class_session?.presenting_user_id && !a.class_session.is_practice;
          bValue = !!b.class_session?.presenting_user_id && !b.class_session.is_practice;
          break;
        case "virtual_class":
          aValue = a.class_session?.category === ClassSessionCategory.VIRTUAL;
          bValue = b.class_session?.category === ClassSessionCategory.VIRTUAL;
          break;
        case "online_users":
          aValue = sectionUserCounts[a.id] || 0;
          bValue = sectionUserCounts[b.id] || 0;
          break;
        default:
          aValue = null;
          bValue = null;
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Sort based on value type
      let comparison = 0;
      if (typeof aValue === "string" && typeof bValue === "string") {
        comparison = aValue.localeCompare(bValue);
      } else if (typeof aValue === "number" && typeof bValue === "number") {
        comparison = aValue - bValue;
      } else if (typeof aValue === "boolean" && typeof bValue === "boolean") {
        comparison = aValue === bValue ? 0 : aValue ? 1 : -1;
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
  }, [filteredSections, sortColumn, sortDirection, sectionUserCounts]);

  // Group sections by organization
  const sectionsByOrg = useMemo(
    () =>
      sortedFilteredSections.reduce(
        (acc, section) => {
          const orgName = section.org.name || "Unknown";
          return {
            ...acc,
            [orgName]: [...(acc[orgName] || []), section],
          };
        },
        {} as Record<string, SectionMonitoringData[]>,
      ),
    [sortedFilteredSections],
  );

  // Calculate key metrics
  const metrics = useMemo(() => {
    const presentingCount = filteredSections.filter(
      (section) =>
        !!section.class_session?.presenting_user_id && !section.class_session.is_practice,
    ).length;

    const virtualClassesCount = filteredSections.filter(
      (section) => section.class_session?.category === ClassSessionCategory.VIRTUAL,
    ).length;

    const totalOnlineUsers = filteredSections.reduce(
      (sum, section) => sum + (sectionUserCounts[section.id] || 0),
      0,
    );

    // Calculate per-org breakdowns for tooltips
    const presentingByOrg = selectedOrgId
      ? {}
      : Object.entries(sectionsByOrg).reduce(
          (acc, [orgName, orgSections]) => {
            const count = orgSections.filter(
              (section) =>
                !!section.class_session?.presenting_user_id && !section.class_session.is_practice,
            ).length;
            if (count > 0) {
              // eslint-disable-next-line no-param-reassign
              acc[orgName] = count;
            }
            return acc;
          },
          {} as Record<string, number>,
        );

    const virtualClassesByOrg = selectedOrgId
      ? {}
      : Object.entries(sectionsByOrg).reduce(
          (acc, [orgName, orgSections]) => {
            const count = orgSections.filter(
              (section) => section.class_session?.category === ClassSessionCategory.VIRTUAL,
            ).length;
            if (count > 0) {
              // eslint-disable-next-line no-param-reassign
              acc[orgName] = count;
            }
            return acc;
          },
          {} as Record<string, number>,
        );

    const onlineUsersByOrg = selectedOrgId
      ? {}
      : Object.entries(sectionsByOrg).reduce(
          (acc, [orgName, orgSections]) => {
            const count = orgSections.reduce(
              (sum, section) => sum + (sectionUserCounts[section.id] || 0),
              0,
            );
            if (count > 0) {
              // eslint-disable-next-line no-param-reassign
              acc[orgName] = count;
            }
            return acc;
          },
          {} as Record<string, number>,
        );

    return {
      presentingCount,
      virtualClassesCount,
      totalOnlineUsers,
      presentingByOrg,
      virtualClassesByOrg,
      onlineUsersByOrg,
    };
  }, [filteredSections, sectionUserCounts, sectionsByOrg, selectedOrgId]);

  // Extract unique orgs from sections for the dropdown
  const orgs = useMemo(
    () =>
      sections.reduce(
        (acc, section) => {
          if (!acc.find((existing) => existing.id === section.org.id)) {
            acc.push(section.org);
          }
          return acc;
        },
        [] as (typeof sections)[0]["org"][],
      ),
    [sections],
  );

  // Reset course selection when org changes
  useEffect(() => {
    setQueryParams({ course: null });
  }, [selectedOrgId, setQueryParams]);

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-lg">Loading sections...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="text-red-600 text-lg">Error: {error}</div>
      </div>
    );
  }

  const handleSort = (
    column: keyof SectionMonitoringData | "presenting" | "virtual_class" | "online_users",
  ) => {
    if (sortColumn === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Set new column with ascending direction
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const getSortLabel = () => {
    const columnLabels: Record<string, string> = {
      title: "Course Title",
      instructor: "Instructor",
      student_count: "Students",
      av_enabled: "AV Enabled",
      av_service: "AV Service",
      presenting: "Presenting",
      virtual_class: "Virtual Class In Session",
      online_users: "Online Users",
    };

    const label = columnLabels[sortColumn] || sortColumn;
    const direction = sortDirection === "asc" ? "↑" : "↓";
    return `${label} ${direction}`;
  };

  return (
    <div className="flex h-full flex-col overflow-hidden">
      <div className="flex-shrink-0 p-6 pb-4">
        <h1 className="text-gray-900 text-2xl font-bold">Courses Monitoring</h1>
        <p className="text-gray-600 mt-2">Overview of all sections across organizations</p>

        <div className="my-4 rounded-lg border bg-white p-4 shadow-sm">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="text-gray-700 text-sm font-medium">Filter by organization:</div>
              <SearchableDropdown
                allOptionLabel="All Organizations"
                getItemDisplayName={(org) => org.name}
                getItemId={(org) => org.id}
                itemIcon={<HomeHouseIcon />}
                items={orgs}
                placeholder="Search organizations..."
                selectedId={selectedOrgId}
                title="Select Organization"
                showAllOption
                onSelect={(org) => setQueryParams({ org: org?.id || null })}
              />

              {selectedOrgId && (
                <>
                  <div className="text-gray-700 text-sm font-medium">Filter by course:</div>
                  <SearchableDropdown
                    allOptionLabel="All Courses"
                    getItemDisplayName={(section) => section.title}
                    getItemId={(section) => section.id}
                    itemIcon={<BookSectionIcon />}
                    items={
                      selectedOrgId
                        ? sections.filter((section) => section.org.id === selectedOrgId)
                        : sections
                    }
                    placeholder="Search sections..."
                    selectedId={selectedSectionId}
                    title="Select Course"
                    showAllOption
                    onSelect={(section) => setQueryParams({ course: section?.id || null })}
                  />
                </>
              )}
            </div>

            <div className="flex items-center gap-8">
              <div className="flex items-center gap-4">
                <div className="text-gray-700 text-sm font-medium">Show active only:</div>
                <Tooltip content="Show only sections that have online users">
                  <div>
                    <Switch
                      as="div"
                      checked={showActiveOnly}
                      className="group flex h-3.5 w-7 min-w-7 cursor-pointer items-center rounded-full bg-black-tint-70 transition data-[checked]:bg-blue data-[checked][data-disabled]:bg-black-tint-70"
                      onChange={() => setQueryParams({ active: showActiveOnly ? "0" : "1" })}
                    >
                      <span className="size-3 translate-x-[0.0675rem] rounded-full bg-white shadow-md transition group-data-[checked]:translate-x-[0.9375rem]" />
                    </Switch>
                  </div>
                </Tooltip>
              </div>

              <div className="flex items-center gap-4">
                <div className="text-gray-700 text-sm font-medium">Sort by:</div>
                <Menu
                  align="end"
                  menuButton={
                    <Button className="gap-2" kind="gray" size="s">
                      {getSortLabel()}
                      <ChevronDownIcon />
                    </Button>
                  }
                  title="Sort Options"
                >
                  <MenuItem
                    className={sortColumn === "title" && sortDirection === "asc" ? "selected" : ""}
                    onClick={() => handleSort("title")}
                  >
                    Course Title ↑
                  </MenuItem>
                  <MenuItem
                    className={sortColumn === "title" && sortDirection === "desc" ? "selected" : ""}
                    onClick={() => {
                      setSortColumn("title");
                      setSortDirection("desc");
                    }}
                  >
                    Course Title ↓
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "instructor" && sortDirection === "asc" ? "selected" : ""
                    }
                    onClick={() => handleSort("instructor")}
                  >
                    Instructor ↑
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "instructor" && sortDirection === "desc" ? "selected" : ""
                    }
                    onClick={() => {
                      setSortColumn("instructor");
                      setSortDirection("desc");
                    }}
                  >
                    Instructor ↓
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "student_count" && sortDirection === "asc" ? "selected" : ""
                    }
                    onClick={() => handleSort("student_count")}
                  >
                    Students ↑
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "student_count" && sortDirection === "desc" ? "selected" : ""
                    }
                    onClick={() => {
                      setSortColumn("student_count");
                      setSortDirection("desc");
                    }}
                  >
                    Students ↓
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "online_users" && sortDirection === "asc" ? "selected" : ""
                    }
                    onClick={() => handleSort("online_users")}
                  >
                    Online Users ↑
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "online_users" && sortDirection === "desc" ? "selected" : ""
                    }
                    onClick={() => {
                      setSortColumn("online_users");
                      setSortDirection("desc");
                    }}
                  >
                    Online Users ↓
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "presenting" && sortDirection === "asc" ? "selected" : ""
                    }
                    onClick={() => handleSort("presenting")}
                  >
                    Presenting ↑
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "presenting" && sortDirection === "desc" ? "selected" : ""
                    }
                    onClick={() => {
                      setSortColumn("presenting");
                      setSortDirection("desc");
                    }}
                  >
                    Presenting ↓
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "virtual_class" && sortDirection === "asc" ? "selected" : ""
                    }
                    onClick={() => handleSort("virtual_class")}
                  >
                    Virtual Class In Session ↑
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "virtual_class" && sortDirection === "desc" ? "selected" : ""
                    }
                    onClick={() => {
                      setSortColumn("virtual_class");
                      setSortDirection("desc");
                    }}
                  >
                    Virtual Class In Session ↓
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "av_enabled" && sortDirection === "asc" ? "selected" : ""
                    }
                    onClick={() => handleSort("av_enabled")}
                  >
                    AV Enabled ↑
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "av_enabled" && sortDirection === "desc" ? "selected" : ""
                    }
                    onClick={() => {
                      setSortColumn("av_enabled");
                      setSortDirection("desc");
                    }}
                  >
                    AV Enabled ↓
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "av_service" && sortDirection === "asc" ? "selected" : ""
                    }
                    onClick={() => handleSort("av_service")}
                  >
                    AV Service ↑
                  </MenuItem>
                  <MenuItem
                    className={
                      sortColumn === "av_service" && sortDirection === "desc" ? "selected" : ""
                    }
                    onClick={() => {
                      setSortColumn("av_service");
                      setSortDirection("desc");
                    }}
                  >
                    AV Service ↓
                  </MenuItem>
                </Menu>
              </div>
            </div>
          </div>
        </div>

        {/* Key Metrics */}
        <div className="my-6">
          <h2 className="text-gray-900 mb-4 text-lg font-semibold">
            {selectedSectionId
              ? `Metrics for ${sections.find((section) => section.id === selectedSectionId)?.title || "Selected Section"}${showActiveOnly ? " (Active Only)" : ""}`
              : selectedOrgId
                ? `Metrics for ${orgs.find((org) => org.id === selectedOrgId)?.name || "Selected Organization"}${showActiveOnly ? " (Active Only)" : ""}`
                : `Metrics Across All Organizations${showActiveOnly ? " (Active Only)" : ""}`}
          </h2>
          <div className="grid grid-cols-3 gap-6">
            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Currently Presenting</p>
                  <p className="text-gray-900 text-3xl font-bold">{metrics.presentingCount}</p>
                </div>
                {Object.keys(metrics.presentingByOrg).length > 0 && (
                  <Tooltip
                    content={`By Organization:\n${Object.entries(metrics.presentingByOrg)
                      .map(([org, count]) => `${org}: ${count}`)
                      .join("\n")}`}
                  >
                    <div className="text-blue-600 cursor-help">
                      <CircleInfoBlueIcon />
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Virtual Classes in Session</p>
                  <p className="text-gray-900 text-3xl font-bold">{metrics.virtualClassesCount}</p>
                </div>
                {Object.keys(metrics.virtualClassesByOrg).length > 0 && (
                  <Tooltip
                    content={`By Organization:\n${Object.entries(metrics.virtualClassesByOrg)
                      .map(([org, count]) => `${org}: ${count}`)
                      .join("\n")}`}
                  >
                    <div className="text-blue-600 cursor-help">
                      <CircleInfoBlueIcon />
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>

            <div className="rounded-lg border bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-gray-600 text-sm font-medium">Total Online Users</p>
                  <p className="text-gray-900 text-3xl font-bold">{metrics.totalOnlineUsers}</p>
                </div>
                {Object.keys(metrics.onlineUsersByOrg).length > 0 && (
                  <Tooltip
                    content={`By Organization:\n${Object.entries(metrics.onlineUsersByOrg)
                      .map(([org, count]) => `${org}: ${count}`)
                      .join("\n")}`}
                  >
                    <div className="text-blue-600 cursor-help">
                      <CircleInfoBlueIcon />
                    </div>
                  </Tooltip>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto px-6 pb-6">
        <div className="space-y-8">
          {Object.entries(sectionsByOrg)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([orgName, orgSections]) => (
              <div className="rounded-lg border bg-white shadow-sm" key={orgName}>
                <div className="bg-gray-50 border-b px-6 py-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h2 className="text-gray-900 text-lg font-semibold">{orgName}</h2>
                      <p className="text-gray-600 text-sm">
                        {orgSections.length} course{orgSections.length !== 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-gray-600 text-sm">AV Enabled for Org:</span>
                      {orgSections[0]?.org.av_enabled ? (
                        <CircleSuccessGreenIcon />
                      ) : (
                        <CircleErrorRedIcon />
                      )}
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="mds-table w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="w-[200px]">Course Title</th>
                        <th className="w-[200px]">Instructor</th>
                        <th className="w-[80px]">Students</th>
                        <th className="w-[80px]">Online Users</th>
                        <th className="w-[100px]">Page Loads</th>
                        <th className="w-[100px]">Websocket Disconnections</th>
                        <th className="w-[100px]">Failed API Requests</th>
                        <th className="w-[100px]">Page Worksheet Load Errors</th>
                        <th className="w-[100px]">Poor Connection Warnings</th>
                        <th className="w-[80px]">Presenting</th>
                        <th className="w-[80px]">Virtual Class In Session</th>
                        <th className="w-[80px]">AV Enabled</th>
                        <th className="w-[120px]">AV Service</th>
                        <th className="w-[240px]">AV Server</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orgSections.map((section) => (
                        <SectionRow
                          key={section.id}
                          section={section}
                          sectionUserCounts={sectionUserCounts}
                        />
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
        </div>

        {filteredSections.length === 0 && (
          <div className="py-12 text-center">
            <p className="text-gray-500">
              {selectedOrgId && selectedSectionId
                ? "No sections found for the selected organization and section."
                : selectedOrgId
                  ? "No sections found for the selected organization."
                  : selectedSectionId
                    ? "No sections found for the selected section."
                    : "No sections found."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};
